import React from 'react';
import { Link } from 'react-router-dom';
import Footer from "../components/layout/Footer";

const highlights = [
  {
    title: 'Last updated',
    description: 'April 10, 2026',
  },
  {
    title: 'Coverage',
    description: 'Website access, application use, APIs, exports, and admin tooling.',
  },
  {
    title: 'Core expectation',
    description: 'Use the platform lawfully, protect account credentials, and respect dataset restrictions.',
  },
];

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance and scope',
    intro:
      'These terms apply to use of the Limbus Intelligence website, research workspace, admin console, APIs, downloadable exports, and any associated support channels.',
    items: [
      'By accessing the platform, creating an account, or calling the APIs, you agree to these terms and to any product-specific notices published inside the service.',
      'If you use the service on behalf of an employer or institution, you represent that you have authority to bind that organization to these terms.',
      'If you do not agree to the terms, you must stop using the service and discontinue API access immediately.',
    ],
  },
  {
    id: 'accounts',
    title: '2. Accounts, eligibility, and security',
    intro:
      'Account integrity is critical because the platform may expose proprietary search workflows, usage history, and restricted datasets.',
    items: [
      'You must provide accurate registration details and keep profile information reasonably current.',
      'You are responsible for maintaining the confidentiality of login credentials, API tokens, and any refresh tokens issued to your account.',
      'You must notify the platform operator promptly if you suspect unauthorized access, credential leakage, or misuse of your workspace.',
      'Accounts may not be shared across unrelated individuals, and role-based admin access must be assigned only to personnel with a valid business need.',
    ],
  },
  {
    id: 'billing',
    title: '3. Subscription, billing, and renewal terms',
    intro:
      'If paid plans or usage-based billing apply, commercial terms are governed by the active order form, plan page, or written commercial agreement.',
    items: [
      'Fees are due according to the billing schedule presented at purchase unless a separate enterprise agreement says otherwise.',
      'Late payment may result in service suspension, export holds, or restrictions on API throughput until the account is brought current.',
      'Unless required by law or explicitly agreed in writing, fees are non-refundable once access has been provisioned for the relevant billing period.',
      'The operator may change pricing for future renewal periods with advance notice communicated through the product, email, or contract amendment.',
    ],
  },
  {
    id: 'use',
    title: '4. Permitted use of the platform and APIs',
    intro:
      'The service is intended for lawful research, diligence, intelligence gathering, discovery, and internal business analysis.',
    items: [
      'You may use the APIs to power approved internal dashboards, workflow automations, and research pipelines that stay within published authentication and rate-control boundaries.',
      'You may not scrape around the platform, bypass access controls, overload the service, introduce malware, probe for vulnerabilities, or reverse engineer non-public implementation details.',
      'You may not use the service to violate law, sanctions restrictions, privacy rights, export controls, or third-party intellectual property rights.',
      'You may not resell raw platform data, expose unrestricted downstream API access to unapproved third parties, or create a competing public dataset mirror from exported content.',
    ],
  },
  {
    id: 'data-rights',
    title: '5. Data sources, intellectual property, and output rights',
    intro:
      'The platform may combine proprietary application code, curated interfaces, and third-party or public-source datasets.',
    items: [
      'The software, branding, interface design, documentation, and non-public product materials remain the property of Limbus Intelligence or its licensors.',
      'Underlying source datasets may be subject to separate public-source terms, partner restrictions, database rights, or attribution obligations, and you are responsible for honoring those requirements when using exports.',
      'Because platform records are extracted and normalized from third-party source materials, some fields may reflect source-side errors, omissions, inconsistencies, or delays.',
      'Subject to payment of applicable fees and compliance with these terms, you retain ownership of your own prompts, user-entered notes, and internally generated workflow configurations.',
      'You may use your results and exports for internal research and decision support, but redistribution of raw records or bulk exports may be restricted by plan, contract, or source-specific licensing.',
    ],
  },
  {
    id: 'privacy',
    title: '6. Privacy, confidentiality, and security',
    intro:
      'Because the platform can process searches, exported records, and account metadata, confidentiality and reasonable security practices are expected from both sides.',
    items: [
      'The operator may collect account details, authentication events, product analytics, search metadata, and support communications to provide, secure, and improve the service.',
      'You should avoid submitting highly sensitive personal data, regulated health data, or confidential materials unless your organization has approved that use case and appropriate safeguards are in place.',
      'Each party must use reasonable administrative, technical, and organizational safeguards to protect confidential information it receives through the service.',
      'Security incidents will be addressed according to applicable law, contract commitments, and the severity of the event.',
    ],
  },
  {
    id: 'compliance',
    title: '7. Compliance and prohibited conduct',
    intro:
      'Use of the platform must remain aligned with law, professional standards, and the intended product purpose.',
    items: [
      'You may not use the service to generate unlawful surveillance, discriminatory profiling, deceptive claims, or regulated advice that requires a licensed human review.',
      'The platform is not a substitute for legal, medical, regulatory, or clinical judgment, even when it surfaces patents, publications, clinical trial records, or FDA material.',
      'You may not remove audit trails, falsify usage records, or interfere with platform monitoring, logging, or access review mechanisms.',
      'The operator may investigate suspected misuse and cooperate with lawful requests from regulators, courts, or government authorities.',
    ],
  },
  {
    id: 'availability',
    title: '8. Availability, maintenance, and product changes',
    intro:
      'The service evolves over time, and some features may be revised, deprecated, or moved behind specific plan tiers.',
    items: [
      'The operator may perform maintenance, deploy updates, change API routes, add security requirements, or modify feature availability as needed to operate the service responsibly.',
      'Reasonable efforts may be made to communicate material breaking changes, especially where they affect active integrations or enterprise customers.',
      'Temporary downtime, degraded performance, or export delays may occur during maintenance windows, infrastructure incidents, or upstream data-source disruptions.',
      'The operator may suspend access to protect the platform, investigate abuse, or comply with law, court order, or contractual restrictions.',
    ],
  },
  {
    id: 'disclaimers',
    title: '9. Warranties, disclaimers, and liability limits',
    intro:
      'Research platforms often depend on third-party data quality, changing source material, and user interpretation, so clear allocation of risk is important.',
    items: [
      'Except to the extent prohibited by law, the service is provided on an as-is and as-available basis without warranties of merchantability, fitness for a particular purpose, non-infringement, or uninterrupted availability.',
      'The operator does not guarantee that every dataset is complete, current, error-free, or suitable for any specific filing, transaction, medical conclusion, legal position, or regulatory submission.',
      'The operator does not warrant that extracted records will be free from inaccuracies inherited from the original source of extraction.',
      'To the fullest extent permitted by law, neither party will be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, goodwill, or anticipated savings.',
      "To the fullest extent permitted by law, the operator's aggregate liability arising from the service will not exceed the amount paid by the customer for the service during the twelve months preceding the claim.",
    ],
  },
  {
    id: 'termination',
    title: '10. Suspension, termination, governing law, and contact',
    intro:
      'These final provisions explain how access can end and how disputes or notices are handled.',
    items: [
      'You may stop using the service at any time. The operator may suspend or terminate access for breach, non-payment, security risk, or legal necessity.',
      'Upon termination, your right to use the service ends immediately, though archival logs, billing records, and legally required retention copies may remain.',
      'If a separate commercial agreement exists, that agreement controls in the event of any conflict with these general website terms.',
      'Questions about these terms, privacy expectations, or enterprise data handling should be directed to the legal or support contact designated by the operator.',
    ],
  },
];

const navLinkClass =
  'rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-300 transition hover:border-blue-400/50 hover:text-white';

const tocLinkClass =
  'rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-medium text-slate-300 transition hover:border-slate-500 hover:text-white';

const LegalDocs = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.16),_transparent_32%),radial-gradient(circle_at_80%_10%,_rgba(59,130,246,0.18),_transparent_26%),linear-gradient(180deg,_rgba(2,6,23,1)_0%,_rgba(2,6,23,1)_100%)]" />

        <header className="sticky top-0 z-20 border-b border-white/10 bg-slate-950/85 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <Link to="/login" className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 shadow-lg shadow-blue-950/40">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="8" stroke="white" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="1.5" fill="white" />
                </svg>
              </span>
              <span>
                <span className="block text-sm font-semibold text-white">Limbus Intelligence</span>
                <span className="block text-xs text-slate-400">Terms, privacy, and usage rules</span>
              </span>
            </Link>

            <div className="flex flex-wrap gap-3">
              <Link to="/api-docs" className={navLinkClass}>
                API Docs
              </Link>
              <Link to="/legal-docs" className={`${navLinkClass} border-blue-400/50 text-white`}>
                Legal Docs
              </Link>
              <Link to="/pricing" className={navLinkClass}>
                Pricing
              </Link>
            </div>
          </div>
        </header>

        <main className="relative">
          <section className="mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 lg:px-8">
            <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
              <div>
                <span className="inline-flex rounded-full border border-sky-400/30 bg-sky-500/10 px-4 py-2 text-sm font-semibold text-sky-100">
                  Legal Docs
                </span>
                <h1 className="mt-6 max-w-4xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                  Terms and conditions for website use, data access, and API integrations
                </h1>
                <p className="mt-6 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg">
                  This page lays out a clean, product-ready legal baseline for platform access,
                  exports, billing, security, acceptable use, privacy expectations, and liability.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a href="#terms" className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-200">
                    Read the terms
                  </a>
                  <Link to="/api-docs" className="rounded-full border border-sky-400/40 bg-sky-500/10 px-5 py-3 text-sm font-semibold text-sky-100 transition hover:border-sky-300 hover:bg-sky-500/15">
                    View API obligations
                  </Link>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  {sections.map((section) => (
                    <a key={section.id} href={`#${section.id}`} className={tocLinkClass}>
                      {section.title.split('. ')[1]}
                    </a>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-1">
                {highlights.map((highlight) => (
                  <article
                    key={highlight.title}
                    className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_24px_60px_-40px_rgba(14,165,233,0.45)]"
                  >
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {highlight.title}
                    </p>
                    <p className="mt-3 text-base font-semibold leading-7 text-white">
                      {highlight.description}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="terms" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] border border-white/10 bg-slate-900/50 p-6 sm:p-8">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-200">
                  Terms and conditions
                </p>
                <p className="mt-3 text-base leading-7 text-slate-300">
                  These terms are written to be usable as a solid product baseline. They are not a
                  substitute for formal legal review where specific jurisdictional, regulatory, or
                  enterprise obligations apply.
                </p>
              </div>

              <div className="mt-8 space-y-6">
                {sections.map((section) => (
                  <article
                    key={section.id}
                    id={section.id}
                    className="rounded-3xl border border-white/10 bg-slate-950/65 p-6 sm:p-8"
                  >
                    <h2 className="text-2xl font-semibold text-white">{section.title}</h2>
                    <p className="mt-4 text-sm leading-7 text-slate-300">{section.intro}</p>

                    <ul className="mt-6 space-y-4 text-sm leading-7 text-slate-300">
                      {section.items.map((item) => (
                        <li key={item} className="flex gap-3">
                          <span className="mt-3 h-2 w-2 rounded-full bg-sky-400" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default LegalDocs;
